import { faPlusCircle, faRedo, faSearch, faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canPerformAction } from 'actions/userActions';
import { useAppDispatch } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { EncodingState, Node } from 'reducers/nodesReducer';
import { getNodes, getNodesEncodings, getUser, getScripts } from 'reducers/selectors';
import { availableOperations, User } from 'reducers/userReducer';
import * as conditionsActions from '../../actions/conditionsActions';
import * as nodesActions from '../../actions/nodesActions';
import * as scriptActions from '../../actions/scriptActions';
import ForwardRulesTable from './forwardRules/ForwardRulesTable';
import DisableNodePopup from './popups/DisableNodePopup';
import EditNodePopup from './popups/EditNodePopup';
import EnableNodePopup from './popups/EnableNodePopup';
import InsertNodePopup from './popups/InsertNodePopup';
import DisabledNodeRow from './rows/DisabledNodeRow';
import EnabledNodeRow from './rows/EnabledNodeRow';
import { Script } from 'reducers/scriptReducer';

export default function NodesPage(): React.ReactElement {
  const dispatch = useAppDispatch();

  const nodes: Node[] = useSelector(getNodes);
  const scripts: Script[] = useSelector(getScripts);
  const nodesEncodings: EncodingState = useSelector(getNodesEncodings);
  const user: User = useSelector(getUser);

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [editingNode, setEditingNode] = useState<Node | null>(null);
  const [enablingNode, setEnablingNode] = useState<Node | null>(null);
  const [disablingNode, setDisablingNode] = useState<Node | null>(null);

  const [filter, setFilter] = useState<string>('');
  const [filteredNodes, setFilteredNodes] = useState<Node[]>(nodes);

  const [insertNewNode, setInsertNewNode] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [restartingServices, setRestartingServices] = useState<boolean>(false);
  const authorized: boolean = useMemo(() => {
    return canPerformAction(user, availableOperations.CAN_MANAGE_NODES);
  }, [user]);

  useEffect(() => {
    dispatch(nodesActions.getNodes());
    dispatch(nodesActions.getNodesEncodings());
    dispatch(nodesActions.getForwardRules());
    dispatch(scriptActions.getAllScripts());
    dispatch(conditionsActions.getConditions());
  }, []);

  useEffect(() => {
    setFilteredNodes(nodes.filter(node => node.aeTitle.toLowerCase().includes(filter.toLowerCase())));
  }, [filter, nodes]);

  const forceReload = () => {
    dispatch(nodesActions.forceGetNodes());
    dispatch(nodesActions.forceGetNodesEncodings());
    dispatch(nodesActions.getForwardRules());
  };

  const restartDicomServices = () => {
    setRestartingServices(true);

    nodesActions
      .restartServices()
      .then(res => {
        let action;
        if (res) {
          action = { type: 'SUCCESS', message: 'services restarted' };
        } else {
          action = { type: 'ERROR', message: 'services not restarted' };
        }
        dispatch(action);
      })
      .finally(() => {
        setRestartingServices(false);
      });
  };

  const insertNode = (node: Node): void => {
    setPending(true);
    nodesActions.insertNode(node).then(
      () => {
        forceReload();
        setPending(false);
        setInsertNewNode(false);
        dispatch({ type: 'SUCCESS', message: `Node '${node.aeTitle}' added` });
      },
      () => {
        setPending(false);
        dispatch({ type: 'ERROR', message: 'Error inserting node' });
      }
    );
  };

  const updateNode = (node: Node): void => {
    setPending(true);
    nodesActions.updateNode(node).then(
      () => {
        forceReload();
        const message = editingNode ? `Node '${node.aeTitle}' updated` : `Node '${node.aeTitle}' enabled`;
        dispatch({ type: 'SUCCESS', message });
        setPending(false);
        setEditingNode(null);
        setEnablingNode(null);
      },
      () => {
        setPending(false);
        dispatch({ type: 'ERROR', message: 'Error updating node' });
      }
    );
  };

  const dicomEcho = (node: Node): void => {
    dispatch({ type: 'INFO', message: `doing dicom echo for node ${node.aeTitle}` });
    nodesActions.dicomEcho(node).then(
      code => {
        if (code === 'SUCCESS') {
          dispatch({ type: 'SUCCESS', message: code });
        } else {
          dispatch({ type: 'ERROR', message: code });
        }
      },
      reject => {
        dispatch({ type: 'ERROR', message: reject.data });
      }
    );
  };

  const disableNode = (): void => {
    setPending(true);
    nodesActions
      .disableNode(disablingNode!)
      .then(forceReload)
      .catch(err => {
        console.error('Error disabling node', err);
        dispatch({ type: 'ERROR', message: 'Error disabling node' });
      })
      .finally(() => setPending(false));
    setDisablingNode(null);
  };

  return (
    <div>
      <Card className="page-panel-common">
        <Card.Body>
          <h3>Nodes</h3>

          <div className="d-flex align-items-baseline justify-content-between">
            <div className="d-flex align-items-baseline justify-content-around">
              <form onSubmit={e => e.preventDefault()} style={{ marginRight: '0.5em' }}>
                <Form.Group>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      name="filter"
                      size="sm"
                      value={filter}
                      onChange={e => setFilter(e.target.value)}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text className="basic-addon2">
                        <FontAwesomeIcon icon={faSearch} />
                      </InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Form.Group>
              </form>

              <Button
                size="sm"
                variant="primary"
                onClick={forceReload}
                style={{ marginLeft: '0.5em', marginRight: '0.5em' }}
              >
                Reload nodes <FontAwesomeIcon icon={faSync} style={{ marginLeft: '5px' }} />
              </Button>

              <Button
                style={{
                  marginLeft: '0.5em',
                  marginRight: '0.5em',
                }}
                disabled={!authorized}
                size="sm"
                variant="primary"
                onClick={() => setInsertNewNode(true)}
              >
                Add new node <FontAwesomeIcon icon={faPlusCircle} style={{ marginLeft: '5px' }} />
              </Button>
            </div>

            <div>
              <Button
                size="sm"
                variant="primary"
                onClick={restartDicomServices}
                disabled={!authorized || restartingServices}
              >
                {restartingServices ? 'Restarting...' : 'Restart DICOM services'}
                <FontAwesomeIcon icon={faRedo} style={{ marginLeft: '5px' }} />
              </Button>
            </div>
          </div>
          <div style={{ marginTop: '3em' }}>
            <Row>
              <Col sm={6}>
                <strong>Enabled nodes</strong>
                <table className="queue-table">
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }} />
                      <th style={{ width: '20%' }} />
                      <th />
                      <th style={{ width: '2em' }} />
                      <th style={{ width: '4.5em' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNodes
                      .filter(it => it.enabled && it.aeTitle !== 'PREFETCH-SERVICE')
                      .map(node => (
                        <EnabledNodeRow
                          key={node.pk}
                          node={node}
                          authorized={authorized}
                          onClick={() => {
                            setSelectedNode(node);
                            dispatch(nodesActions.isTouchedNode());
                          }}
                          onEdit={() => setEditingNode(node)}
                          onDisable={() => {
                            setDisablingNode(node);
                          }}
                        />
                      ))}
                    {filteredNodes
                      .filter(node => node.enabled && node.aeTitle === 'PREFETCH-SERVICE')
                      .map(node => (
                        <tr
                          key={node.pk}
                          className="node-row"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedNode(node)}
                        >
                          <td>
                            <strong title={node.aeTitle}>{node.aeTitle}</strong>
                          </td>
                          <td>-</td>
                          <td>
                            <i>Special node used by the Prefetch Service</i>
                          </td>
                          <td />
                        </tr>
                      ))}
                  </tbody>
                </table>

                <strong>Disabled nodes</strong>
                <table className="queue-table">
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }} />
                      <th style={{ width: '20%' }} />
                      <th style={{ width: '45%' }} />
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNodes
                      .filter(it => !it.enabled)
                      .map(node => (
                        <DisabledNodeRow
                          authorized={authorized}
                          key={node.pk}
                          node={node}
                          onEnable={() => setEnablingNode(node)}
                        />
                      ))}
                  </tbody>
                </table>
              </Col>
              <Col sm={6}>
                <ForwardRulesTable selectedNode={selectedNode} />
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

      {enablingNode && (
        <EnableNodePopup
          node={enablingNode}
          onClose={() => setEnablingNode(null)}
          onSave={updateNode}
          pending={pending}
          aetsAlreadyTaken={nodes.filter(node => node.aeTitle !== enablingNode.aeTitle).map(node => node.aeTitle)}
          onDicomEchoClick={dicomEcho}
          scripts={scripts}
        />
      )}

      {editingNode && (
        <EditNodePopup
          node={editingNode}
          encodings={nodesEncodings[editingNode.aeTitle]}
          onClose={() => setEditingNode(null)}
          onSave={updateNode}
          pending={pending}
          aetsAlreadyTaken={nodes.filter(node => node.aeTitle !== editingNode.aeTitle).map(node => node.aeTitle)}
          onDicomEchoClick={dicomEcho}
          scripts={scripts}
        />
      )}

      {insertNewNode && (
        <InsertNodePopup
          onClose={() => setInsertNewNode(false)}
          onSave={insertNode}
          pending={pending}
          aetsAlreadyTaken={nodes.map(node => node.aeTitle)}
          nodes={nodes.map(node => node.aeTitle)}
          onDicomEchoClick={dicomEcho}
          scripts={scripts}
        />
      )}
      {disablingNode && (
        <DisableNodePopup
          node={disablingNode}
          onClose={() => {
            setDisablingNode(null);
          }}
          onSave={disableNode}
        />
      )}
    </div>
  );
}
